import React, { forwardRef, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Carousel3D, { Carousel3DChange, Carousel3DHandle } from './Carousel3D';
import { useCardHeight } from '../hooks/useCardHeight';
import type { EmblaCarouselType } from '../vendor/embla-carousel';

export interface MediaCarouselProps<T> {
  items: T[];
  renderItem: (
    item: T,
    index: number,
    selectedIndex: number,
    ctx: {
      isMobile: boolean;
      carouselRadius: number;
      tiltEnabled: boolean;
      ref?: React.Ref<HTMLDivElement>;
    }
  ) => React.ReactNode;
  onSelect?: (item: T) => void;
  onBack?: () => void;
  hideBackButton?: boolean;
  vinylTintColor?: string;
  vinylTintOpacity?: number;
  initialIndex?: number;
  onReInit?: (index: number) => void;
  onInit?: (api: EmblaCarouselType) => void;
  onOverlayChange?: (props: {
    slideHeight: number;
    carouselRadius: number;
    tintColor?: string;
    tintOpacity?: number;
  }) => void;
  tiltEnabled?: boolean;
  forceMobileLayout?: boolean;
}

function MediaCarouselInner<T>({
  items,
  renderItem,
  onSelect,
  onBack,
  hideBackButton,
  vinylTintColor,
  vinylTintOpacity = 0,
  initialIndex,
  onReInit,
  onInit,
  onOverlayChange,
  tiltEnabled = true,
  forceMobileLayout,
}: MediaCarouselProps<T>, ref: React.Ref<Carousel3DHandle>) {
  const longPressRef = useRef<number>();
  const [carouselState, setCarouselState] = useState<Carousel3DChange>({
    selectedIndex: 0,
    isMobile: false,
    carouselRadius: 0,
  });
  const { isMobile, carouselRadius } = carouselState;
  const lastCardHeight = useRef(0);
  const { ref: cardRef, height: cardHeight } = useCardHeight(
    isMobile,
    carouselRadius,
    lastCardHeight.current,
  );
  useEffect(() => {
    lastCardHeight.current = cardHeight;
  }, [cardHeight]);

  useEffect(() => {
    if (!isMobile && (cardHeight <= 0 || carouselRadius <= 0)) return;
    onOverlayChange?.({
      slideHeight: isMobile ? 0 : cardHeight,
      carouselRadius: isMobile ? 0 : carouselRadius,
      tintColor: vinylTintColor,
      tintOpacity: vinylTintOpacity,
    });
  }, [
    cardHeight,
    carouselRadius,
    vinylTintColor,
    vinylTintOpacity,
    isMobile,
    onOverlayChange,
  ]);

  const handleContextMenu = (evt: React.MouseEvent): void => {
    if (onBack) evt.preventDefault();
  };

  const startLongPress = (): void => {
    if (!onBack) return;
    clearTimeout(longPressRef.current);
    longPressRef.current = window.setTimeout(() => {
      onBack();
    }, 600);
  };

  const clearLongPress = (): void => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = undefined;
    }
  };

  return (
    <div
      className="relative z-0"
      onContextMenu={onBack ? handleContextMenu : undefined}
      onTouchStart={onBack ? startLongPress : undefined}
      onTouchEnd={onBack ? clearLongPress : undefined}
      onTouchMove={onBack ? clearLongPress : undefined}
      onTouchCancel={onBack ? clearLongPress : undefined}
    >
      <div className="relative isolate">
        <Carousel3D
          ref={ref}
          items={items}
          onSelect={onSelect}
          onBack={onBack}
          hideBackButton={hideBackButton}
          onChange={setCarouselState}
          initialIndex={initialIndex}
          onReInit={onReInit}
          onInit={onInit}
          forceMobileLayout={forceMobileLayout}
          backLabel="Back"
          tiltEnabled={tiltEnabled}
          renderItem={(item, index, selectedIndex, ctx) => (
            <div
              key={index}
              className={clsx(
                'embla__slide p-4',
                !ctx.isMobile && 'absolute left-1/2 top-1/2'
              )}
              style={{
                transform: !ctx.isMobile ? 'translate(-50%, -50%)' : undefined,
                transformStyle: ctx.isMobile ? 'flat' : 'preserve-3d',
              }}
            >
              <div
                style={{
                  transformStyle: ctx.isMobile ? 'flat' : 'preserve-3d',
                }}
              >
                {renderItem(item, index, selectedIndex, {
                  ...ctx,
                  ref: index === 0 ? cardRef : undefined,
                })}
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}

const MediaCarousel = forwardRef(MediaCarouselInner) as <T>(
  props: MediaCarouselProps<T> & { ref?: React.Ref<Carousel3DHandle> }
) => React.ReactElement;

export default MediaCarousel;

